import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import MoleculeStepper, {moleculeStepperDesign} from '@s-ui/react-molecule-stepper'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

const LayoutPageContent = ({
  banner,
  buttonsConfig,
  children,
  footer,
  helpBox,
  isColumnReverse = false,
  stepperConfig,
  subtitle,
  title
}) => {
  const {
    primaryCta: {fullWidth: primaryCtaFullWidth = true},
    secondaryCta: {fullWidth: secondaryCtaFullWidth = true} = {},
    primaryCta = {},
    secondaryCta = {}
  } = buttonsConfig

  const hasStepper = Boolean(stepperConfig)
  const hasBanner = Boolean(banner)
  const hasFooter = Boolean(footer)
  const hasHelpBox = Boolean(helpBox)
  const hasPrimaryCta = primaryCta && Boolean(primaryCta.text)
  const hasSecondaryCta = secondaryCta && Boolean(secondaryCta.text)
  const hasSubtitle = typeof subtitle === 'string'
  const hasTitle = typeof title === 'string'

  const className = cx({
    'mt-LayoutPageContent--columnReverse': isColumnReverse,
    'mt-LayoutPageContent': !isColumnReverse
  })

  return (
    <div className={className}>
      {hasTitle && (
        <div className="mt-LayoutPageContent-title">
          <TitleBasic as={titleBasicTags.h2} color={titleBasicColors.black} size={titleBasicSizes.l} title={title} />
        </div>
      )}
      {isColumnReverse && hasHelpBox && <div className="mt-LayoutPageContent-aside">{helpBox}</div>}
      <div className="mt-LayoutPageContent-main">
        {hasStepper && (
          <MoleculeStepper
            design={moleculeStepperDesign.COMPRESSED}
            steps={stepperConfig?.steps}
            step={stepperConfig?.step}
            labels={stepperConfig?.labels}
          />
        )}
        {hasBanner && <div className="mt-LayoutPageContent-banner">{banner}</div>}
        {hasSubtitle && (
          <TitleBasic as={titleBasicTags.h2} color={titleBasicColors.black} size={titleBasicSizes.s} title={subtitle} />
        )}
        {hasHelpBox && <div className="mt-LayoutPageContent-helpBoxMobile">{helpBox}</div>}
        {children}
        <div className="mt-LayoutPageContent-buttonContainer">
          {hasSecondaryCta && (
            <div className="mt-LayoutPageContent-button">
              <AtomButton
                {...secondaryCta}
                fullWidth={secondaryCtaFullWidth}
                onClick={secondaryCta.handleOnClick}
                shape={atomButtonShapes.CIRCULAR}
                size={atomButtonSizes.LARGE}
              >
                {secondaryCta.text}
              </AtomButton>
            </div>
          )}
          {hasPrimaryCta && (
            <div className="mt-LayoutPageContent-button">
              <AtomButton
                {...primaryCta}
                fullWidth={primaryCtaFullWidth}
                onClick={primaryCta.handleOnClick}
                shape={atomButtonShapes.CIRCULAR}
                size={atomButtonSizes.LARGE}
              >
                {primaryCta.text}
              </AtomButton>
            </div>
          )}
        </div>
        {hasFooter && footer}
      </div>
      {!isColumnReverse && hasHelpBox && <div className="mt-LayoutPageContent-aside">{helpBox}</div>}
    </div>
  )
}

LayoutPageContent.displayName = 'LayoutPageContent'
LayoutPageContent.propTypes = {
  banner: PropTypes.node,
  buttonsConfig: PropTypes.shape({
    primaryCta: PropTypes.shape({
      color: PropTypes.string,
      design: PropTypes.string,
      fullWidth: PropTypes.bool,
      handleOnClick: PropTypes.func,
      isLoading: PropTypes.bool,
      text: PropTypes.string
    }),
    secondaryCta: PropTypes.shape({
      color: PropTypes.string,
      design: PropTypes.string,
      fullWidth: PropTypes.bool,
      handleOnClick: PropTypes.func,
      isLoading: PropTypes.bool,
      text: PropTypes.string
    })
  }),
  children: PropTypes.element,
  footer: PropTypes.node,
  helpBox: PropTypes.node,
  isColumnReverse: PropTypes.bool,
  stepperConfig: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string),
    step: PropTypes.number,
    steps: PropTypes.number
  }),
  subtitle: PropTypes.string,
  title: PropTypes.string
}

export default LayoutPageContent
